import React, { useEffect, useRef, useState } from 'react';
import { AppWrap } from '../../wrapper';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { client, urlFor } from '../../client';
import images from '../../constants/images';
import './About.scss';

const fallbackAbouts = [
	{
		title: 'Web Development',
		description:
			'Robust, scalable full-stack builds — from clean APIs to blazing-fast frontends.',
		img: images.WebDevelopment,
	},
	{
		title: 'Web Design',
		description:
			'Bold, modern layouts with cinematic detail that make brands unforgettable.',
		img: images.webDesign,
	},
	{
		title: 'UI / UX',
		description:
			'Interfaces engineered around real users — intuitive, accessible, conversion-ready.',
		img: images.UIUX,
	},
	{
		title: 'Web Animation',
		description:
			'Motion with meaning: 3D, parallax and micro-interactions that bring products to life.',
		img: images.WebAnimation,
	},
];

const stats = [
	{ value: 6, suffix: '+', label: 'Years Experience' },
	{ value: 80, suffix: '+', label: 'Projects Shipped' },
	{ value: 25, suffix: '+', label: 'Happy Clients' },
];

// Odometer reel: full 0-9 revolutions spun through before landing on the digit.
const SPINS = 1;
const REEL = Array.from({ length: (SPINS + 1) * 10 }, (_, i) => i % 10);

// Every column stacks all ten digits, so all columns render at the same width.
// That lets each one carry the slice of a gradient spanning the whole number:
// scale the background to `columns` widths and shift it one column per index.
const gradientSlice = (index, columns) => ({
	'--n': columns,
	'--pos': columns > 1 ? `${(index / (columns - 1)) * 100}%` : '0%',
});

// The strip translates by a percentage of its own height, so one cell is
// exactly 100 / REEL.length percent — no pixel math tied to the font size.
const Digit = ({ digit, start, delay, index, columns }) => {
	const reduceMotion = useReducedMotion();
	const offset = ((SPINS * 10 + digit) / REEL.length) * 100;

	return (
		<span className='stat-digit' style={gradientSlice(index, columns)}>
			<motion.span
				className='stat-digit-strip'
				initial={{ y: '0%' }}
				animate={{ y: start ? `-${offset}%` : '0%' }}
				transition={
					reduceMotion
						? { duration: 0 }
						: { duration: 1.7, delay, ease: [0.16, 1, 0.3, 1] }
				}
			>
				{REEL.map((n, i) => (
					<span key={i}>{n}</span>
				))}
			</motion.span>
		</span>
	);
};

const SlideCounter = ({ value, suffix, start, delay = 0 }) => {
	const digits = String(value).split('').map(Number);
	const columns = digits.length + (suffix ? 1 : 0);

	return (
		<span className='stat-value' aria-label={`${value}${suffix || ''}`}>
			<span className='stat-digits' aria-hidden='true'>
				{digits.map((digit, i) => (
					<Digit
						key={i}
						digit={digit}
						start={start}
						delay={delay + i * 0.09}
						index={i}
						columns={columns}
					/>
				))}
			</span>
			{suffix && (
				<span
					className='stat-suffix'
					aria-hidden='true'
					style={gradientSlice(digits.length, columns)}
				>
					{suffix}
				</span>
			)}
		</span>
	);
};

const gridStagger = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const cardReveal = {
	hidden: { y: 70, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const fadeUp = {
	hidden: { y: 40, opacity: 0 },
	visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

// Fluent-style "reveal" — a soft light follows the cursor across the card surface
const RevealCard = ({ children, className = '' }) => {
	const ref = useRef(null);

	const handleMouseMove = (e) => {
		const el = ref.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
		el.style.setProperty('--my', `${e.clientY - rect.top}px`);
	};

	return (
		<div ref={ref} className={className} onMouseMove={handleMouseMove}>
			{children}
		</div>
	);
};

const About = () => {
	const [abouts, setAbouts] = useState([]);
	const statsRef = useRef(null);
	const statsInView = useInView(statsRef, { once: true, amount: 0.5 });

	useEffect(() => {
		let mounted = true;
		client
			.fetch('*[_type == "abouts"]')
			.then((data) => {
				if (mounted) setAbouts(data && data.length ? data : fallbackAbouts);
			})
			.catch(() => {
				if (mounted) setAbouts(fallbackAbouts);
			});
		return () => {
			mounted = false;
		};
	}, []);

	return (
		<div className='about-content'>
			<div className='about-glow' aria-hidden='true' />

			<div className='about-intro'>
				<motion.div
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.4 }}
				>
					<p className='eyebrow'>About Me</p>
					<h2 className='about-heading'>
						I know that <span className='text-gradient'>good design</span>
						<br />
						means <em>good business</em>
					</h2>
				</motion.div>

				<motion.div
					className='about-bio'
					variants={fadeUp}
					initial='hidden'
					whileInView='visible'
					viewport={{ once: true, amount: 0.4 }}
				>
					<p>
						I'm a fullstack web developer who blends engineering precision with cinematic
						design. From React and Next.js apps to WordPress builds, I ship digital
						products that look premium, load fast and convert.
					</p>
				</motion.div>
			</div>

			<motion.div
				className='about-stats'
				ref={statsRef}
				variants={fadeUp}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.4 }}
			>
				{stats.map((stat, index) => (
					<div className='stat' key={stat.label}>
						<SlideCounter
							value={stat.value}
							suffix={stat.suffix}
							start={statsInView}
							delay={0.25 + index * 0.13}
						/>
						<span className='stat-label'>{stat.label}</span>
					</div>
				))}
			</motion.div>

			<motion.div
				className='about-grid'
				variants={gridStagger}
				initial='hidden'
				whileInView='visible'
				viewport={{ once: true, amount: 0.15 }}
			>
				{abouts.map((about, index) => (
					<motion.div
						className='about-card-wrap'
						variants={cardReveal}
						key={about.title + index}
					>
						<RevealCard className='about-card'>
							<span className='about-card-num'>0{index + 1}</span>
							<div className='about-card-icon'>
								<img
									src={about.imgUrl ? urlFor(about.imgUrl) : about.img}
									alt={about.title}
								/>
							</div>
							<h4>{about.title}</h4>
							<p>{about.description}</p>
						</RevealCard>
					</motion.div>
				))}
			</motion.div>
		</div>
	);
};

export default AppWrap(About, 'about');
